import React from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { CoreButton, CoreIconButton } from 'src/components/core'
import { Icon } from '@iconify/react'
import TextFieldPopup from 'src/components/textfield-popup'
import SuggestionSkeleton from 'src/components/suggestion-skeleton'
import { withStyles } from '@material-ui/core/styles'
import { isMobile } from 'react-device-detect'
import styles from '../styles'

const GoalItem = ({
  classes,
  goal,
  sectionIndex,
  moduleIndex,
  goalIndex,
  goalRefs,
  onGoalChange,
  onRemoveGoal,
  onGoalFocus,
  onKeyDown,
  popupStates,
  anchorElements,
  fieldSuggestions,
  popupLoadingStates,
  onRegenerateSuggestions,
  onPopupClose,
  onSuggestionClick,
}) => {
  const goalKey = `${sectionIndex}-${moduleIndex}-${goalIndex}`
  const isPopupOpen = popupStates[goalKey] || false
  const anchorEl = anchorElements[goalKey]
  const suggestions = fieldSuggestions[goalKey] || []
  const isLoading = popupLoadingStates[goalKey] || false
  const courseTopic = useSelector(state => state.aICreator.topic)

  return (
    <Box className={classes.goalItem}>
      <Typography className={classes.goalNumber}>{goalIndex + 1}.</Typography>

      <div
        ref={el => {
          if (!goalRefs.current) goalRefs.current = []
          goalRefs.current[goalKey] = el
        }}
        contentEditable
        suppressContentEditableWarning
        className={classes.goalText}
        data-placeholder="Type a goal or select from suggested goals"
        onBlur={() => {
          const goalText = goalRefs.current[goalKey]?.textContent
          if (goal !== goalText) {
            onGoalChange(goalText, sectionIndex, moduleIndex, goalIndex)
          }
        }}
        onFocus={() => onGoalFocus(sectionIndex, moduleIndex, goalIndex)}
        onKeyDown={e => onKeyDown(e, sectionIndex, moduleIndex, goalIndex)}
        style={{ cursor: 'text' }}
      >
        {goal}
      </div>

      <TextFieldPopup
        anchorEl={anchorEl}
        open={isPopupOpen}
        onClose={() => onPopupClose(goalKey)}
        placement="bottom-start"
        offset={4}
        maxHeight={250}
      >
        <div className={classes.popupContent}>
          <div className={classes.popupTitleContainer}>
            <Typography
              fontSize={14}
              fontWeight={400}
              className={classes.popupTitle}
            >
              Suggested course goals:
            </Typography>
            {suggestions.length > 0 && (
              <CoreButton
                size="small"
                variant="text"
                color="secondary"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon icon="hugeicons:exchange-01" fontSize={16} />
                  )
                }
                onClick={() =>
                  onRegenerateSuggestions(sectionIndex, moduleIndex, goalIndex)
                }
                disabled={isLoading}
              >
                {isLoading ? 'Generating...' : 'Regenerate'}
              </CoreButton>
            )}
          </div>

          <List dense className={classes.suggestionList}>
            {isLoading ? (
              <SuggestionSkeleton />
            ) : suggestions.length > 0 ? (
              suggestions.map((suggestion, suggestionIndex) => (
                <ListItem
                  key={suggestionIndex}
                  button
                  className={classes.suggestionItem}
                  onClick={() => {
                    onSuggestionClick(
                      sectionIndex,
                      moduleIndex,
                      goalIndex,
                      suggestion,
                    )
                  }}
                >
                  <div className={classes.suggestionItemIcon}>
                    <img
                      src="/assets/AI-Assistant-icon.svg"
                      width={isMobile ? 16 : 24}
                      height={isMobile ? 16 : 24}
                      alt="AI Assistant"
                    />
                  </div>
                  <ListItemText
                    primary={suggestion}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      className: classes.suggestionText,
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body2" style={{ color: '#637381' }}>
                  {courseTopic?.trim()
                    ? suggestions && suggestions.length === 0
                      ? 'No suggestions available. Try regenerating.'
                      : 'Suggestions will appear here.'
                    : 'Enter a course topic to get suggestions.'}
                </Typography>
              </div>
            )}
          </List>
        </div>
      </TextFieldPopup>
      <CloseIcon
        fontSize="small"
        style={{
          cursor: 'pointer',
          color: '#838D98',
          fontSize: '20px',
        }}
        onClick={() => onRemoveGoal(sectionIndex, moduleIndex, goalIndex)}
      />
    </Box>
  )
}

export default withStyles(styles)(GoalItem)
